import { MenuItem, MenuItemCommandEvent } from "primeng/api";
import { Observable } from "rxjs";

// Helper type that picks all the keys in T *except* for "catch-all" properties, like
// '[key: string]: any' properties.
type NoStringIndex<T> = { [K in keyof T as string extends K ? never : K]: T[K] };

type RestrictedMenuItem = NoStringIndex<MenuItem>;

export type AppMenuItem = AppMenuLeaf | AppMenuBranch | AppMenuNavigableBranch | AppMenuAsyncNavigableBranch;

/**
 * A sidebar menu item that has no children, and has an associated URL.
 */
export interface AppMenuLeaf extends Omit<RestrictedMenuItem, "state" | "items" | "routerLink"> {
  // MenuItem types this as 'any', but the sidebar makes the assumption that if it's there,
  // it's always an array of strings. Let's enforce that.
  routerLink?: string[];
  notificationCount?: number;
}

/**
 * A sidebar menu item that has children, but no associated URL or URL prefix.
 */
export interface AppMenuBranch extends AppMenuLeaf {
  items: AppMenuItem[];
}

/**
 * A sidebar menu item that has children, and its direct children have an associated URL,
 * which means that this item has a URL prefix.
 */
export interface AppMenuNavigableBranch extends AppMenuBranch {
  state: AppMenuItemState;
}

/**
 * Associated state with an `AppMenuNavigableBranch` that contains the URL prefix of all
 * the menu item's children.
 */
export interface AppMenuItemState {
  urlPrefix: string;
}

/**
 * A sidebar menu item that has children, and its direct children have an associated URL,
 * which means that this item has a URL prefix.
 * This menu item's children are loaded asynchronously via its state.loadItems() callback.
 */
export interface AppMenuAsyncNavigableBranch extends Omit<AppMenuBranch, "state" | "command"> {
  command: (e: MenuItemCommandEvent) => void;
  state: AppMenuItemAsyncState;
}

/**
 * Associated state with an `AppMenuAsyncNavigableBranch`. Contains the URL prefix of all the menu item's
 * children, a callback to load those children, and a flag indicating whether or not the children
 * have already been loaded.
 */
export interface AppMenuItemAsyncState extends AppMenuItemState {
  loadItems: (parent: AppMenuAsyncNavigableBranch) => Observable<AppMenuItem[]>;
  isLoaded: boolean;
}

export function isAppMenuBranch(item: MenuItem): item is AppMenuBranch {
  return "items" in item && Array.isArray(item.items);
}

export function isAppMenuNavigableBranch(item: MenuItem): item is AppMenuNavigableBranch {
  return isAppMenuBranch(item) && "state" in item && item.state !== undefined && isAppMenuItemState(item.state);
}

export function isAppMenuItemState(state: unknown): state is AppMenuItemState {
  return typeof state === "object" && state !== null && "urlPrefix" in state && typeof state["urlPrefix"] === "string";
}

export function isAppMenuAsyncNavigableBranch(item: MenuItem): item is AppMenuAsyncNavigableBranch {
  return isAppMenuBranch(item) && "state" in item && item.state !== undefined && isAppMenuItemAsyncState(item.state);
}

export function isAppMenuItemAsyncState(state: unknown): state is AppMenuItemAsyncState {
  return (
    typeof state === "object" &&
    state !== null &&
    "urlPrefix" in state &&
    typeof state["urlPrefix"] === "string" &&
    "loadItems" in state &&
    state["loadItems"] !== undefined &&
    typeof state["loadItems"] === "function" &&
    "isLoaded" in state &&
    typeof state["isLoaded"] === "boolean"
  );
}
